import { useEffect, useMemo, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { rosterApi } from '../api/auth';
import type { RosterRow } from '../api/auth';
import { Button } from './Button';
import { TextField } from './Field';
import { Avatar } from './ParticipantChip';

/**
 * Search the unit's roster and pick somebody — one definition, two callers (TROOP-180).
 *
 * **Searched, never listed.** The dev troop carries 410 people and a real one carries a hundred, so
 * a roster rendered whole is the bug CLAUDE.md names three times over — a screen that draws every
 * row it is given. Nothing appears until somebody types, and at most {@link SHOWN} match.
 *
 * **It pre-judges nothing.** The server runs the two-adult quorum, the Pack rule and I5 on the
 * result, and each refusal arrives with a sentence somebody can act on. Filtering this list down to
 * "people who would be allowed" would put a second copy of those rules on the client, drifting from
 * the first the day one of them changes.
 *
 * Extracted rather than copied: `ParticipantsPanel` wrote it first (TROOP-178) and the topic form
 * needed the same control, and two implementations of *search the roster and pick somebody* is two
 * answers to what the control does — including the empty state, which has to say both of its
 * nothings ("nobody matches" and "everybody who matches is already in").
 */
const SHOWN = 6;

export function RosterPicker({
  unitId,
  label = 'Search the roster',
  actionLabel = 'Add',
  /** Who is already accounted for. They are filtered out rather than shown and refused. */
  exclude,
  onPick,
  busyPersonId,
}: {
  unitId: string;
  label?: string;
  actionLabel?: string;
  exclude: Set<string>;
  /** Called with the whole row, because a caller usually needs the name as well as the id. */
  onPick: (row: RosterRow) => void;
  /** The id currently being acted on, so its control can say so. */
  busyPersonId?: string | null;
}) {
  const [roster, setRoster] = useState<RosterRow[] | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let live = true;

    rosterApi
      .list(unitId)
      .then((rows) => live && setRoster(rows))
      .catch(() => live && setRoster([]));

    return () => {
      live = false;
    };
  }, [unitId]);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length < 2 || roster === null) return [];

    return roster
      .filter((r) => !exclude.has(r.personId))
      .filter((r) => r.displayName.toLowerCase().includes(needle))
      .slice(0, SHOWN);
  }, [query, roster, exclude]);

  return (
    <div>
      <TextField
        label={label}
        search
        type="text"
        value={query}
        // Never trimmed in the handler: these boxes are controlled, so trimming here eats the
        // trailing space before the next character arrives and the space bar appears dead.
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Start typing a name"
        autoComplete="off"
      />

      {query.trim().length >= 2 && matches.length === 0 && (
        <p
          className="t-caption"
          style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-3) 0 0' }}
        >
          Nobody on the roster matches, or everyone who does is already here.
        </p>
      )}

      <ul style={{ listStyle: 'none', margin: 'var(--space-3) 0 0', padding: 0 }}>
        {matches.map((row) => (
          <li
            key={row.personId}
            className="flex items-center gap-3"
            style={{ minHeight: 'var(--tap-min)' }}
          >
            <Avatar
              initials={initialsOf(row.displayName)}
              classification={row.classification === 'Youth' ? 'youth' : 'adult'}
            />
            <span className="t-body flex-1" style={{ color: 'var(--color-text-primary)' }}>
              {row.displayName}
            </span>
            <Button
              variant="secondary"
              onClick={() => {
                onPick(row);
                setQuery('');
              }}
              {...(busyPersonId === row.personId
                ? { disabledReason: 'Adding them now.' }
                : {})}
            >
              <UserPlus size={15} aria-hidden="true" />
              {actionLabel}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const initialsOf = (name: string) =>
  name.split(' ').map((part) => part[0]).slice(0, 2).join('');
